/**
 * Cognito OIDC configuration builder
 *
 * @deprecated This module is deprecated in favor of provider-agnostic config/auth.ts.
 * Functions here are maintained for backward compatibility but will be removed in a future version.
 * Use the following instead:
 * - `loadAuthConfig()` from config/auth.ts for provider-agnostic configuration
 * - `getOidcClientConfig()` from config/auth.ts for OIDC client configuration
 */

import type { EnvironmentConfig, OidcConfig } from './types';

/**
 * Convert EnvironmentConfig to OIDC client configuration
 * Compatible with oidc-client-ts and react-oidc-context
 *
 * @deprecated Use `getOidcClientConfig()` from config/auth.ts instead.
 */
export function toOidcConfig(env: EnvironmentConfig): OidcConfig {
  return {
    authority: env.cognitoAuthority,
    client_id: env.cognitoClientId,
    redirect_uri: env.redirectUri,
    post_logout_redirect_uri: env.postLogoutRedirectUri,
    response_type: 'code', // Authorization code flow with PKCE
    scope: env.scope,
    loadUserInfo: true,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 300, // Notify 5 minutes before expiration
  };
}

/**
 * Validate OIDC configuration for common issues
 *
 * @deprecated Use provider-agnostic validation instead.
 */
export function validateOidcConfig(config: OidcConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!config.authority) {
    errors.push('OIDC authority is required');
  }

  if (!config.client_id) {
    errors.push('OIDC client_id is required');
  }

  if (!config.redirect_uri) {
    errors.push('OIDC redirect_uri is required');
  }

  if (!config.scope.includes('openid')) {
    errors.push('OIDC scope must include "openid"');
  }

  if (config.response_type !== 'code') {
    errors.push('OIDC response_type must be "code" for PKCE flow');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Get default OIDC configuration for testing/development
 *
 * @deprecated Use `loadAuthConfig()` from config/auth.ts instead.
 */
export function getDefaultOidcConfig(): OidcConfig {
  return {
    authority: '',
    client_id: 'mock',
    redirect_uri: window.location.origin + '/',
    post_logout_redirect_uri: window.location.origin + '/',
    response_type: 'code',
    scope: 'openid email profile',
    loadUserInfo: true,
    automaticSilentRenew: true,
  };
}
